import { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";
import { Eyebrow, Reveal, SectionTitle } from "@/components/site/Primitives";
import { CheckList, DiscoveryBand, PageHero } from "@/components/site/Blocks";
import { useT } from "@/lib/i18n";

export default function Audience({ kind }) {
  const t = useT();
  const c = t[kind];
  const img = kind === "moving" ? "/img/bled.jpg" : "/img/ljubljana-canal.jpg";
  const side = kind === "moving" ? "/img/izola-harbour.jpg" : "/img/koper-street.jpg";
  return (
    <div data-testid={`${kind}-page`}>
      <PageHero eyebrow={c.eyebrow} title={c.title} intro={c.intro} image={img} imageAlt={c.imageAlt} />

      <section data-testid={`${kind}-services`} aria-label={t.audience.servicesLabel} className="container-x pb-20 sm:pb-24">
        <ul className="grid gap-6 md:grid-cols-2">
          {c.services.map((s, i) => (
            <li key={s.title} data-testid={`${kind}-service-${i}`} className="card-soft p-7 sm:p-8">
              <h3 className="font-display text-[1.65rem] leading-tight text-forest">{s.title}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-forest/75">{s.text}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-ivory-2 py-20 sm:py-24">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5"><SectionTitle title={c.questionsTitle} /></Reveal>
          <ul className="space-y-3 lg:col-span-7">
            {c.questions.map((q, i) => (
              <Reveal key={q} delay={i * 0.05}>
                <QuestionItem q={q} a={c.answers?.[i]} id={`${kind}-q-${i}`} />
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <Shared a={t.audience} kind={kind} />

      <section className="container-x py-20 sm:py-28">
        <Reveal><SectionTitle title={c.stepsTitle} /></Reveal>
        <ol className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {c.steps.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.07}>
              <li className="relative h-full border-t border-forest/20 pt-7" data-testid={`${kind}-step-${i}`}>
                <span className="font-display text-5xl italic text-gold-text">0{i + 1}</span>
                <h3 className="mt-4 font-display text-[1.65rem] leading-tight text-forest">{s.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-forest/75">{s.text}</p>
              </li>
            </Reveal>
          ))}
        </ol>
      </section>

      <section className="container-x pb-4">
        <div className="grid items-stretch gap-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <img src={side} alt="" loading="lazy" className="h-72 w-full rounded-[32px] object-cover sm:h-full sm:min-h-[420px]" />
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.08}>
            <div className="card-soft h-full p-8 sm:p-12">
              <Eyebrow>{c.eyebrow}</Eyebrow>
              <h2 className="mt-5 font-display text-4xl leading-tight text-forest sm:text-5xl">{c.helpTitle}</h2>
              <div className="mt-8"><CheckList items={c.help} /></div>
              <p className="mt-10 border-l-2 border-gold pl-4 text-sm leading-relaxed text-forest/75" data-testid={`${kind}-disclaimer`}>{c.note}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <DiscoveryBand title={c.ctaTitle} />
    </div>
  );
}

function QuestionItem({ q, a, id }) {
  const [open, setOpen] = useState(false);
  const row = "flex w-full items-start gap-4 px-6 py-5 text-left text-[15px] leading-relaxed text-forest sm:text-base";
  const icon = <HelpCircle size={20} className="mt-0.5 shrink-0 text-gold-text" strokeWidth={1.5} />;
  if (!a) return <li className={`${row} rounded-2xl bg-ivory-card`} data-testid={id}>{icon}{q}</li>;
  return (
    <li className="rounded-2xl bg-ivory-card" data-testid={id}>
      <button type="button" className={row} aria-expanded={open} aria-controls={`${id}-a`} onClick={() => setOpen(!open)} data-testid={`${id}-toggle`}>
        {icon}
        <span className="flex-1">{q}</span>
        <ChevronDown size={18} aria-hidden="true" className={`mt-1 shrink-0 text-forest/60 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div id={`${id}-a`} hidden={!open} data-testid={`${id}-answer`} className="px-6 pb-5 pl-[3.75rem] text-[15px] leading-relaxed text-forest/75">{a}</div>
    </li>
  );
}

function Shared({ a, kind }) {
  return (
    <section data-testid={`${kind}-shared`} className="container-x grid gap-14 py-20 sm:py-24 lg:grid-cols-3 lg:gap-12">
      <div data-testid={`${kind}-process`}>
        <h3 className="font-display text-3xl leading-tight text-forest sm:text-4xl">{a.processTitle}</h3>
        <ol className="mt-8 space-y-6">
          {a.process.map((s, i) => (
            <li key={s.title} className="flex gap-4 border-t border-forest/15 pt-5">
              <span className="w-10 shrink-0 font-display text-3xl italic leading-none text-gold-text">0{i + 1}</span>
              <div>
                <p className="font-semibold text-forest">{s.title}</p>
                <p className="mt-1 text-[15px] leading-relaxed text-forest/75">{s.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
      <div data-testid={`${kind}-outcomes`}>
        <h3 className="font-display text-3xl leading-tight text-forest sm:text-4xl">{a.outcomesTitle}</h3>
        <div className="mt-8"><CheckList items={a.outcomes} /></div>
      </div>
      <div data-testid={`${kind}-limits`}>
        <h3 className="font-display text-3xl leading-tight text-forest sm:text-4xl">{a.limitsTitle}</h3>
        <p className="mt-8 border-l-2 border-gold pl-4 text-[15px] leading-relaxed text-forest/75">{a.limits}</p>
      </div>
    </section>
  );
}
